import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { ProductAnalyticsService } from '@vendasta/product-analytics';
import { Observable, Subject } from 'rxjs';
import { take, takeUntil, tap } from 'rxjs/operators';
import { FieldHandler } from '../auxiliary-data-table/auxiliary-data-table.interface';
import { AuxiliaryFieldHandlerService, LoadingStatus, SavingStatus } from '../auxiliary-field-handler.service';
import { ObjectType } from '../schema-service/schema-interface';

const SNACKBAR_DURATION = 4000;

@Component({
  selector: 'aux-auxiliary-data-card',
  templateUrl: './auxiliary-data-card.component.html',
  styleUrls: ['./auxiliary-data-card.component.scss'],
})
export class AuxiliaryDataCardComponent implements OnInit, OnDestroy {
  @Input() partnerId = '';
  @Input() objectType: ObjectType;
  @Input() objectId = '';
  @Input() auxiliaryDataAdminURL = '';

  @Output() pendingCustomFieldsChanges: EventEmitter<boolean> = new EventEmitter<boolean>();

  formGroup = new UntypedFormGroup({});
  LoadingStatus = LoadingStatus;

  loadingStatus$ = this.auxiliaryDataService.loadingStatus$;
  savingStatus$ = this.auxiliaryDataService.savingStatus$;

  fieldHandlers$!: Observable<FieldHandler[]>;

  SavingStatus = SavingStatus;

  savedFormValue: object;
  killSubscriptions$$: Subject<boolean> = new Subject();

  constructor(
    private readonly auxiliaryDataService: AuxiliaryFieldHandlerService,
    private snackService: SnackbarService,
    private readonly trackingService: ProductAnalyticsService,
  ) {}

  ngOnInit(): void {
    this.auxiliaryDataService.init(this.partnerId, this.objectType, this.objectId, true);
    this.fieldHandlers$ = this.auxiliaryDataService.fieldHandlers$.pipe(
      tap((handlers) => {
        handlers.forEach((handler) => {
          this.formGroup.setControl(handler.fieldId, handler.formControl);
        });
        this.savedFormValue = this.formGroup.getRawValue();
      }),
    );

    this.formGroup.valueChanges.pipe(takeUntil(this.killSubscriptions$$)).subscribe((value) => {
      const isEqualToInitial = JSON.stringify(value) === JSON.stringify(this.savedFormValue);
      this.pendingCustomFieldsChanges.emit(!isEqualToInitial && !this.formGroup.pristine);
    });
  }

  saveChanges(): void {
    this.posthogTrackCustomFieldEvent('start');
    this.auxiliaryDataService
      .saveChanges()
      .pipe(take(1))
      .subscribe({
        next: (fieldsSaved) => {
          if (fieldsSaved) {
            this.snackService.openSuccessSnack('AUXILIARY_DATA_CARD.SAVE.SUCESS');
            this.posthogTrackCustomFieldEvent('success');
            this.formGroup.markAsPristine();
            this.pendingCustomFieldsChanges.emit(false);
            this.savedFormValue = this.formGroup.getRawValue();
          } else {
            this.savingError();
          }
        },
        error: () => {
          this.savingError();
        },
      });
  }

  private savingError() {
    this.snackService.openErrorSnack('AUXILIARY_DATA_CARD.SAVE.ERROR', {
      duration: SNACKBAR_DURATION,
    });
    this.posthogTrackCustomFieldEvent('fail');
  }

  private posthogTrackCustomFieldEvent(suffix: string): void {
    this.trackingService.trackEvent(
      `CustomFields:edit-${this.objectType}-custom-field-data-${suffix}`,
      'CustomFields',
      `edit-${this.objectType}-custom-field-data-${suffix}`,
    );
  }

  ngOnDestroy(): void {
    this.killSubscriptions$$.next(true);
    this.killSubscriptions$$.complete();
  }
}
